/**
 * @module avatar/types
 *
 * Core TypeScript contracts for the Avatar & Habitat Studio cosmetic system.
 * Defines the four-layer cosmetic taxonomy:
 *   1. Frame — rarity border HUD overlay
 *   2. Background — lighting / time-of-day atmospheric layer
 *   3. User Avatar — silhouette, garment, hand-rig composites
 *   4. Landscape — habitat plot with anchor coordinates
 *
 * Plus critter attachment and pre-render URL manifests.
 */

// ──────────────────────────────────────────────
// Cosmetic Layer Taxonomy
// ──────────────────────────────────────────────

/** The six atomic layer types tracked in the cosmetic catalog. */
export type CosmeticLayerType =
  | 'frame'
  | 'avatar_frame'
  | 'background'
  | 'avatar_body'
  | 'avatar_base'
  | 'avatar_eyes'
  | 'avatar_mouth'
  | 'avatar_hair'
  | 'avatar_clothing'
  | 'avatar_outfit'
  | 'avatar_hat'
  | 'avatar_accessory'
  | 'avatar_hand'
  | 'critter'
  | 'landscape';

/** Rarity tiers governing cosmetic item glow, border, and drop-rate logic. */
export type CosmeticRarity =
  | 'standard'
  | 'uncommon'
  | 'rare'
  | 'epic'
  | 'legendary';

/**
 * Normalizes an arbitrary database category/layer_type string (e.g. from catalog.assets or hub.cosmetic_items)
 * into a recognized atomic CosmeticLayerType matching inspector tabs and renderer layers.
 */
export function normalizeLayerType(rawCategory: string | undefined | null): CosmeticLayerType {
  if (!rawCategory) return 'avatar_base';
  const clean = rawCategory.toLowerCase().trim().replace(/[- ]/g, '_');
  switch (clean) {
    case 'base':
    case 'avatar_base':
      return 'avatar_base';
    case 'body':
    case 'avatar_body':
      return 'avatar_body';
    case 'hair':
    case 'avatar_hair':
      return 'avatar_hair';
    case 'eye':
    case 'eyes':
    case 'avatar_eyes':
      return 'avatar_eyes';
    case 'mouth':
    case 'avatar_mouth':
      return 'avatar_mouth';
    case 'clothing':
    case 'garment':
    case 'garments':
    case 'avatar_clothing':
      return 'avatar_clothing';
    case 'outfit':
    case 'outfits':
    case 'avatar_outfit':
      return 'avatar_outfit';
    case 'hat':
    case 'hats':
    case 'headwear':
    case 'avatar_hat':
      return 'avatar_hat';
    case 'accessory':
    case 'accessories':
    case 'avatar_accessory':
      return 'avatar_accessory';
    case 'hand':
    case 'hands':
    case 'hand_rig':
    case 'handrig':
    case 'avatar_hand':
      return 'avatar_hand';
    case 'frame':
    case 'frames':
    case 'avatar_frame':
      return 'avatar_frame';
    case 'background':
    case 'backgrounds':
      return 'background';
    case 'companion':
    case 'companions':
    case 'critter':
    case 'critters':
      return 'critter';
    case 'landscape':
    case 'landscapes':
    case 'plot':
    case 'terrain':
      return 'landscape';
    default:
      if (clean.startsWith('avatar_')) {
        return clean as CosmeticLayerType;
      }
      return `avatar_${clean}` as CosmeticLayerType;
  }
}

// ──────────────────────────────────────────────
// Catalog Item
// ──────────────────────────────────────────────

/**
 * A single cosmetic catalog entry stored in `hub.cosmetic_items`.
 * Items are referenced by a stable text ID (e.g. "frame_neon_cyan").
 */
export interface CosmeticItem {
  /** Stable text key, e.g. "frame_neon_cyan", "hand_clockwork_brass". */
  id: string;

  /** Which compositing layer this item occupies. */
  layerType: CosmeticLayerType;

  /** Human-readable display name. */
  name: string;

  /** CDN URL to the raw asset file (PNG / WebP / SVG). */
  assetUrl: string;

  /** Drop-rate / visual tier. Defaults to 'standard'. */
  rarity: CosmeticRarity;

  /** Arbitrary per-item metadata (animation frames, glow color, etc.). */
  metadata: Record<string, unknown>;
}

// ──────────────────────────────────────────────
// Avatar Configuration
// ──────────────────────────────────────────────

/** User avatar body + hand-rig + clothing composite configuration. */
export interface AvatarConfig {
  /** Selected body silhouette asset ID / base ID. */
  silhouetteId: string;

  /** Optional hand-rig accessory asset ID. */
  handRigId?: string;

  /** Optional tint hex color applied to the silhouette (e.g. "#FF9900"). */
  tintColor?: string;

  /** Optional clothing / garment overlay asset ID. */
  clothingId?: string;

  /** Optional modular eyes asset ID. */
  eyesId?: string;

  /** Optional modular mouth asset ID. */
  mouthId?: string;

  /** Optional modular hair asset ID. */
  hairId?: string;

  /** Optional modular hat asset ID. */
  hatId?: string;

  /** Optional modular glasses / accessory asset ID. */
  accessoryId?: string;
}

// ──────────────────────────────────────────────
// Critter Configuration
// ──────────────────────────────────────────────

/** Allowed attachment positions for a critter companion. */
export type CritterPerchLocation = 'shoulder' | 'pocket' | 'hover' | 'ground';

/** Configuration for a single attached critter or companion object. */
export interface CritterConfig {
  /** Critter asset ID. */
  critterId: string;

  /** Optional shell / armor overlay for the critter. */
  shellId?: string;

  /** Optional headwear overlay for the critter. */
  headwearId?: string;

  /** Where the critter is attached relative to the avatar. */
  perchLocation: CritterPerchLocation;
}

// ──────────────────────────────────────────────
// Landscape Configuration
// ──────────────────────────────────────────────

/** 2D anchor coordinates for avatar placement within a landscape. */
export interface AnchorCoordinates {
  x: number;
  y: number;
}

/** Landscape time-of-day theme. */
export type LandscapeTheme = 'day' | 'night';

/** Landscape habitat plot configuration. */
export interface LandscapeConfig {
  /** Selected landscape asset ID. */
  landscapeId: string;

  /** Avatar anchor placement within the landscape scene. */
  anchorCoordinates: AnchorCoordinates;

  /** Time-of-day atmospheric theme. */
  theme: LandscapeTheme;
}

// ──────────────────────────────────────────────
// Pre-Render URL Manifest
// ──────────────────────────────────────────────

/**
 * URLs to pre-rendered composite images generated by the edge worker.
 * Downstream clients consume only these flattened WebP files — never raw layers.
 */
export interface RenderUrls {
  /** 128×128 avatar chip for feed cards, chat bubbles, etc. */
  chip: string;

  /** 512×512 profile image for profile pages and settings. */
  profile: string;

  /** 1920×1080 full landscape render for habitat views. */
  landscape: string;

  /** Monotonically increasing version counter for cache busting. */
  version: number;
}

// ──────────────────────────────────────────────
// User Cosmetic Loadout (Root Contract)
// ──────────────────────────────────────────────

/**
 * The complete cosmetic loadout for a single user.
 * Persisted in `hub.user_cosmetic_loadouts.loadout` as JSONB.
 */
export interface UserCosmeticLoadout {
  /** Owner user UUID (references auth.users). */
  userId: string;

  /** Selected frame (rarity border HUD) asset ID. */
  frameId: string;

  /** Selected background (lighting/atmosphere) asset ID. */
  backgroundId: string;

  /** Avatar body, hand-rig, and clothing configuration. */
  avatarConfig: AvatarConfig;

  /** Attached critter / companion object configurations. Empty array if none equipped. */
  critters: CritterConfig[];

  /** Landscape habitat plot configuration. */
  landscapeConfig: LandscapeConfig;

  /** Pre-rendered composite image URLs and cache version. */
  renderUrls: RenderUrls;
}

// ──────────────────────────────────────────────
// Compositing Bounds & Layer Helpers
// ──────────────────────────────────────────────

/** Pixel-space bounding box for a compositing layer. */
export interface CompositingBounds {
  /** X offset from the canvas origin. */
  x: number;
  /** Y offset from the canvas origin. */
  y: number;
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
}

/** Z-order rendering specification for a single asset layer. */
export interface AssetLayer {
  /** Reference to a CosmeticItem.id. */
  itemId: string;

  /** Layer type for compositing order resolution. */
  layerType: CosmeticLayerType;

  /** Rendering bounds within the composite canvas. */
  bounds: CompositingBounds;

  /** Z-index for draw ordering (higher = front). */
  zIndex: number;

  /** Opacity (0–1) applied during compositing. */
  opacity: number;
}

/** Full compositing manifest consumed by the pre-render edge worker. */
export interface CompositeManifest {
  /** Ordered list of asset layers to composite (back → front). */
  layers: AssetLayer[];

  /** Target output dimensions in pixels. */
  outputWidth: number;
  outputHeight: number;

  /** Requested output format. */
  format: 'webp' | 'png';

  /** JPEG/WebP quality (1–100). */
  quality: number;
}
